import { serverInfo } from "../config/serverConfig";
import { checkResponse } from "../utils/apiError";


import { apiFetch } from "./apiFetch";

async function readJsonResponse(resp, fallbackMessage) {
  checkResponse(resp);
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    throw new Error(data?.message || fallbackMessage);
  }
  return data;
}

/** Fetch the read-only templates owned by the platform System account. */
export async function fetchSystemTemplateProjects() {
  const resp = await apiFetch(serverInfo.serverUrl + "/getSystemProject", {
    method: "POST",
    body: JSON.stringify({}),
  });
  const data = await readJsonResponse(resp, "Could not load system templates");
  if (!Array.isArray(data)) {
    throw new Error("Could not load system templates: invalid response");
  }
  return data;
}

/**
 * Create an active project and cloned task hierarchy from an account or System template.
 * @param {{ account_id: string, template_project_id: string, template_scope: "account" | "system", name: string, start_date: string, created_by?: string }} params
 */
export async function createProjectFromTemplate(params) {
  const account_id = params?.account_id != null ? String(params.account_id).trim() : "";
  const template_project_id =
    params?.template_project_id != null ? String(params.template_project_id).trim() : "";
  const template_scope = params?.template_scope != null ? String(params.template_scope).trim() : "";
  const name = params?.name != null ? String(params.name).trim() : "";
  const start_date = params?.start_date != null ? String(params.start_date).trim() : "";
  const created_by = params?.created_by != null ? String(params.created_by).trim() : "";
  if (!account_id) throw new Error("account_id is required");
  if (!template_project_id) throw new Error("template_project_id is required");
  if (!["account", "system"].includes(template_scope)) throw new Error("template_scope must be account or system");
  if (!name) throw new Error("Project name is required");
  if (!start_date) throw new Error("Start date is required");

  const resp = await apiFetch(serverInfo.serverUrl + "/createProjectFromTemplate", {
    method: "POST",
    body: JSON.stringify({
      account_id,
      template_project_id,
      template_scope,
      name,
      start_date,
      created_by,
    }),
  });
  return readJsonResponse(resp, "Could not create project from template");
}

/**
 * Preview counts for deleteProject (projects, tasks, sub-tasks, time blocks).
 * @param {{ account_id: string, _id?: string, _ids?: string[] }} params
 * @returns {Promise<{ projects: number, tasks: number, subTasks: number, timeBlocks: number }>}
 */
export async function fetchDeleteProjectImpact(params) {
  const account_id = params.account_id != null ? String(params.account_id).trim() : "";
  if (!account_id) {
    throw new Error("account_id is required");
  }
  const body = { account_id };
  if (Array.isArray(params._ids) && params._ids.length > 0) {
    body._ids = params._ids.map((x) => String(x || "").trim()).filter(Boolean);
  } else if (params._id != null && String(params._id).trim() !== "") {
    body._id = String(params._id).trim();
  } else {
    throw new Error("_id or _ids is required");
  }
  const resp = await apiFetch(serverInfo.serverUrl + "/getDeleteProjectImpact", {
    method: "POST",
    body: JSON.stringify(body),
  });
  checkResponse(resp);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Could not load delete preview");
  }
  return resp.json();
}

/** Human-readable bullet list for project delete confirmation dialogs. */
export function formatDeleteProjectImpactSummary(impact) {
  if (!impact || typeof impact.projects !== "number") {
    return "Related tasks, sub-tasks, and time blocks will also be permanently deleted.";
  }
  return (
    "Records to delete:\n" +
    `• Projects (parent): ${impact.projects}\n` +
    `• Tasks: ${impact.tasks}\n` +
    `• Sub-tasks: ${impact.subTasks}\n` +
    `• Time blocks (scheduled / calendar): ${impact.timeBlocks}`
  );
}
